import React, { useEffect, useMemo, useState } from 'react';
import { apiService, RollOption, RollOptions } from '../../../services/api';
import { intentLabel } from '../../../types/discipline';
import './RollTicket.css';

/**
 * Roll order ticket (Oct 2026). One panel, one decision, one confirm:
 *   legs (BTC current · STO selected · NET) → choose the new contract (the
 *   engine's target first, alternatives from the SAME engine) → checks →
 *   the intent's alternative to rolling → Confirm roll.
 * Choosing a row never executes; only "Confirm roll" does.
 */

interface Props {
  position: { id: string; ticker: string; positionType?: string };
  onClose: () => void;
  /** Executes the roll at the user's confirmed fills; resolves with the new leg id. */
  onConfirm: (o: RollOption, ctx: { buyback: number; sell: number; stock: number }) => Promise<string | null>;
}

const fmtK = (k: number) => (Number.isInteger(k) ? String(k) : k.toFixed(2).replace(/0$/, ''));
const code = (iso: string, k: number, put: boolean) => `${iso.slice(5, 7)}/${iso.slice(8, 10)} ${fmtK(k)}${put ? 'P' : 'C'}`;
const money = (v: number) => `${v < 0 ? '−' : '+'}$${Math.abs(Math.round(v)).toLocaleString('en-US')}`;
const shortDate = (iso: string) => new Date(iso.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const RollTicket: React.FC<Props> = ({ position, onClose, onConfirm }) => {
  const [data, setData] = useState<RollOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selKey, setSelKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The fills the user confirms (prefilled with the last trades); these are
  // what the roll records, never silently the marks.
  const [buyFill, setBuyFill] = useState('');
  const [sellFill, setSellFill] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiService.getRollOptions(position.id)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        const first = d.options.find((o) => o.is_target) ?? d.options.find((o) => o.viable);
        setSelKey(first ? `${first.expiry}-${first.strike}` : null);
      })
      .catch(() => { if (!cancelled) setError("Couldn't load roll options. Try again."); });
    return () => { cancelled = true; };
  }, [position.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const put = (data?.position_type ?? position.positionType) === 'cash_secured_put';
  const sel = useMemo(() => data?.options.find((o) => `${o.expiry}-${o.strike}` === selKey) ?? null, [data, selKey]);
  useEffect(() => { setBuyFill(data?.buyback_price != null ? data.buyback_price.toFixed(2) : ''); }, [data]);
  useEffect(() => { setSellFill(sel?.sell_price != null ? sel.sell_price.toFixed(2) : ''); }, [sel]);
  const num = (v: string) => (v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : null);
  const buyNum = num(buyFill);
  const sellNum = num(sellFill);
  const stock = data?.stock_price ?? null;
  const fillsOk = buyNum != null && sellNum != null && sellNum > 0 && stock != null;

  const confirm = async () => {
    if (!data || !sel || !sel.viable || busy || !fillsOk) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(sel, { buyback: buyNum as number, sell: sellNum as number, stock: stock as number });
    } catch {
      setError('The roll did not go through. Nothing was changed.');
      setBusy(false);
    }
  };

  const checks = (o: RollOption, d: RollOptions) => {
    const out: Array<{ ok: boolean; text: string }> = [];
    out.push(d.next_earnings_date
      ? (o.crosses_earnings
        ? { ok: false, text: `Holds through earnings ${shortDate(d.next_earnings_date)}` }
        : { ok: true, text: `Expires before earnings ${shortDate(d.next_earnings_date)}` })
      : { ok: true, text: 'No earnings date before expiry' });
    out.push(o.extension <= d.max_roll_extension
      ? { ok: true, text: `+${o.extension}d, within your ${d.max_roll_extension}d roll cap` }
      : { ok: false, text: `+${o.extension}d, beyond your ${d.max_roll_extension}d roll cap` });
    out.push(o.above_basis
      ? { ok: true, text: `Strike ${fmtK(o.strike)} ${put ? '≤' : '≥'} net basis ${d.net_basis.toFixed(2)}` }
      : { ok: false, text: `Strike ${fmtK(o.strike)} ${put ? 'above' : 'below'} net basis ${d.net_basis.toFixed(2)}: locks in a loss if assigned` });
    out.push(o.open_interest != null && o.open_interest >= 50
      ? { ok: true, text: `Open interest ${o.open_interest.toLocaleString('en-US')}` }
      : { ok: false, text: `Thin: open interest ${o.open_interest ?? '—'} (rule ≥ 50)` });
    out.push(o.net_credit >= d.min_roll_credit
      ? { ok: true, text: `Credit ${o.net_credit.toFixed(2)}/sh ≥ ${d.min_roll_credit.toFixed(2)} minimum` }
      : { ok: false, text: `Credit ${o.net_credit.toFixed(2)}/sh below the ${d.min_roll_credit.toFixed(2)} minimum: not worth rolling` });
    return out;
  };

  return (
    <div className="rt-overlay" onClick={() => !busy && onClose()}>
      <div className="rt" role="dialog" aria-modal="true" aria-label={`Roll ${position.ticker}`} onClick={(e) => e.stopPropagation()}>
        <header className="rt-head">
          <div>
            <h2>Roll <b>{position.ticker}</b>{data && <span className="rt-dim"> {code(data.expiry, data.strike, put)} ×{data.contracts}</span>}</h2>
            {data && (
              <p className="rt-sub">
                {intentLabel(data.intent, put)}
                {data.rule_name ? <> · your rule <b>{data.rule_name}</b> → {data.rule_action}</> : <> · no rule fired (manual roll)</>}
              </p>
            )}
          </div>
          <button type="button" className="rt-x" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </header>

        {!data && !error && <div className="rt-body rt-empty">Loading roll options…</div>}
        {error && !data && <div className="rt-body rt-empty is-err">{error}</div>}

        {data && (
          <div className="rt-body">
            {/* Order */}
            <section className="rt-sec">
              <div className="rt-h">Order</div>
              <div className="rt-order">
                <span>BTC</span><span>{data.contracts} {position.ticker} {code(data.expiry, data.strike, put)}</span>
                <b className="rt-fill"><input type="number" min="0" step="0.01" inputMode="decimal" value={buyFill} disabled={busy}
                  onChange={(e) => setBuyFill(e.target.value)} aria-label="Bought back at (your fill, per share)" /></b>
                <span>STO</span><span>{sel ? `${data.contracts} ${position.ticker} ${code(sel.expiry, sel.strike, put)}` : '—'}</span>
                <b className="rt-fill"><input type="number" min="0" step="0.01" inputMode="decimal" value={sellFill} disabled={busy || !sel}
                  onChange={(e) => setSellFill(e.target.value)} aria-label="Sold at (your fill, per share)" /></b>
                <span>NET</span>
                <span className={sel && sel.net_credit >= 0 ? 'is-gain' : 'is-loss'}>
                  {sel ? `${Math.abs(sel.net_credit).toFixed(2)} ${sel.net_credit >= 0 ? 'credit' : 'debit'}/sh` : '—'}
                </span>
                <b className={sel && sel.net_credit >= 0 ? 'is-gain' : 'is-loss'}>{sel ? money(sel.net_credit_total) : '—'}</b>
              </div>
              <p className="rt-note">Prefilled with the last trades, not live quotes. Enter your actual fills: they are what the roll records. NET is at the last trades.{stock == null ? ' No stock price yet: the roll can be saved once the Price is in.' : ''}</p>
            </section>

            {/* Choose */}
            <section className="rt-sec">
              <div className="rt-h">New contract</div>
              {data.options.length === 0 ? (
                <p className="rt-note">No roll candidates within your rules right now.</p>
              ) : (
                <div className="rt-table" role="radiogroup" aria-label="New contract">
                  <div className="rt-tr rt-th"><span>Contract</span><span>DTE</span><span>Δ</span><span>Credit/sh</span><span>Credit</span><span>OI</span><span /></div>
                  {data.options.map((o) => {
                    const k = `${o.expiry}-${o.strike}`;
                    const on = k === selKey;
                    return (
                      <button
                        type="button" key={k} role="radio" aria-checked={on} disabled={!o.viable || busy}
                        className={`rt-tr${on ? ' is-on' : ''}${o.viable ? '' : ' is-off'}`}
                        onClick={() => setSelKey(k)}
                      >
                        <span className="rt-c">{o.is_target && <i className="rt-star" title="Your rule's target">★</i>}{code(o.expiry, o.strike, put)}</span>
                        <span>{o.dte}d <em>+{o.extension}</em></span>
                        <span>{o.delta.toFixed(2)}</span>
                        <span className={o.net_credit >= 0 ? 'is-gain' : 'is-loss'}>{o.net_credit.toFixed(2)}</span>
                        <span className={o.net_credit >= 0 ? 'is-gain' : 'is-loss'}>{money(o.net_credit_total)}</span>
                        <span>{o.open_interest != null ? o.open_interest.toLocaleString('en-US') : '—'}</span>
                        <span className="rt-flags">
                          {o.crosses_earnings && <i className="rt-flag" title="Holds through the earnings report">EARN</i>}
                          {!o.viable && <i className="rt-flag is-off" title={o.reason ?? ''}>{o.reason?.startsWith('credit') ? 'MIN CR' : o.reason?.startsWith('DTE') ? 'CAP' : o.reason?.startsWith('holds') ? 'EARN' : 'RULE'}</i>}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {data.options.some((o) => !o.viable) && (
                <ul className="rt-why">
                  {data.options.filter((o) => !o.viable).map((o) => (
                    <li key={`${o.expiry}-${o.strike}`}>{code(o.expiry, o.strike, put)}: not offered by your rules ({o.reason})</li>
                  ))}
                </ul>
              )}
            </section>

            {/* Checks */}
            {sel && (
              <section className="rt-sec">
                <div className="rt-h">Checks</div>
                <ul className="rt-checks">
                  {checks(sel, data).map((c, i) => <li key={i} className={c.ok ? 'is-ok' : 'is-warn'}>{c.ok ? '✓' : '!'} {c.text}</li>)}
                </ul>
              </section>
            )}

            {/* The intent's alternative */}
            <section className="rt-sec">
              <div className="rt-h">Instead of rolling</div>
              <div className="rt-alt">
                <b>{data.alternative.label}</b>
                <span className="rt-dim">{data.alternative.detail}</span>
                {data.alternative.cost_total != null && <span>cost {money(-data.alternative.cost_total)}</span>}
                {data.alternative.pnl_total != null && <span>leg P/L <b className={data.alternative.pnl_total >= 0 ? 'is-gain' : 'is-loss'}>{money(data.alternative.pnl_total)}</b></span>}
              </div>
            </section>

            {error && <p className="rt-err" role="alert">{error}</p>}
          </div>
        )}

        <footer className="rt-foot">
          <button type="button" className="rt-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="rt-btn is-pri" onClick={confirm} disabled={!sel || !sel.viable || busy || !fillsOk}>
            {busy ? 'Rolling…' : 'Confirm roll at my fills'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default RollTicket;
